import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Activity, ArrowDownUp, ListTodo, CheckCircle2, AlertTriangle, Server } from "lucide-react";
import { api } from "../lib/api";

const fmtRate = (kbps) => (kbps == null ? "—" : `${((kbps * 8) / 1e6).toFixed(1)} Gbps`);

const Dot = ({ status }) => (
  <span className="relative flex w-2.5 h-2.5 shrink-0">
    {status === "operational" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />}
    <span className={`relative inline-flex rounded-full w-2.5 h-2.5 ${status === "operational" ? "bg-emerald-400" : "bg-amber-400"}`} />
  </span>
);

const Bar = ({ pct, danger }) => (
  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-700 ${danger ? "bg-rose-400" : "bg-gradient-to-r from-cyan-400 to-emerald-400"}`}
      style={{ width: `${Math.min(100, pct)}%` }}
    />
  </div>
);

function RouteCard({ r }) {
  return (
    <div className="glass rounded-2xl p-4 space-y-3" data-testid={`route-card-${r.name.replace(" ", "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <p className="font-display font-semibold text-xs text-white flex items-center gap-1.5">
          <Server size={12} className="text-cyan-400" /> {r.name}
        </p>
        <span className="flex items-center gap-1.5 text-[10px]">
          <Dot status={r.status} />
          <span className={r.status === "operational" ? "text-emerald-300" : "text-amber-300"}>
            {r.status === "operational" ? "Operacional" : "Sob carga"}
          </span>
        </span>
      </div>
      <div>
        <div className="flex justify-between text-[10px] mb-1">
          <span className="text-slate-500">CPU</span>
          <span className="font-mono2 text-slate-300">{r.cpu_percent}%</span>
        </div>
        <Bar pct={r.cpu_percent} danger={r.cpu_percent > 80} />
      </div>
      <div>
        <div className="flex justify-between text-[10px] mb-1">
          <span className="text-slate-500">RAM</span>
          <span className="font-mono2 text-slate-300">{r.mem_used_gb}/{r.mem_total_gb} GB</span>
        </div>
        <Bar pct={r.mem_percent} danger={r.mem_percent > 85} />
      </div>
      <div className="border-t border-slate-800/60 pt-2.5 space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-400 flex items-center gap-1 font-mono2" data-testid={`route-bw-${r.name.replace(" ", "-").toLowerCase()}`}>
            <ArrowDownUp size={10} className="text-slate-500" /> ↓{fmtRate(r._down)} ↑{fmtRate(r._up)}
          </span>
          <span className="text-slate-500 flex items-center gap-1 font-mono2">
            <ListTodo size={10} /> {r.jobs_running + r.jobs_queued}
          </span>
        </div>
        <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-600">Porta dedicada {r.port_gbps} Gbps</p>
      </div>
    </div>
  );
}

export default function Status() {
  const [data, setData] = useState(null);
  const prevRef = useRef({});

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data: d } = await api.get("/system/public-status");
        if (!alive) return;
        for (const r of d.routes || []) {
          const prev = prevRef.current[r.name];
          if (prev) {
            const dt = Math.max(0.5, r.ts - prev.ts);
            r._down = Math.max(0, (r.net_recv_kb - prev.net_recv_kb) / dt);
            r._up = Math.max(0, (r.net_sent_kb - prev.net_sent_kb) / dt);
          }
          prevRef.current[r.name] = r;
        }
        setData(d);
      } catch (e) { /* mantém o último estado em caso de falha */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const ok = data?.overall === "operational";
  const updatedAt = data?.updated_at ? new Date(data.updated_at).toLocaleTimeString("pt-PT") : "…";
  const routeCount = data?.routes?.length || 0;

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-10" data-testid="status-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center">
              <Zap size={18} className="text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">CINE<span className="neon-text">BOT</span></span>
          </Link>
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Status · atualiza a cada 5s</span>
        </div>

        <div
          data-testid="status-banner"
          className={`rounded-2xl border px-5 py-4 flex items-center gap-3 ${ok ? "border-emerald-400/30 bg-emerald-400/10" : "border-amber-400/30 bg-amber-400/10"}`}
        >
          {ok ? <CheckCircle2 size={20} className="text-emerald-400 shrink-0" /> : <AlertTriangle size={20} className="text-amber-400 shrink-0" />}
          <div>
            <p className={`text-sm font-semibold ${ok ? "text-emerald-200" : "text-amber-200"}`} data-testid="status-banner-text">
              {ok ? "Todos os sistemas operacionais" : "Instabilidade parcial — estamos monitorando"}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {routeCount} rotas online · Última verificação às {updatedAt}
            </p>
          </div>
        </div>

        <div className="glass rounded-2xl overflow-hidden" data-testid="status-services">
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <Activity size={14} className="text-cyan-400" />
            <p className="text-[11px] font-mono2 uppercase tracking-widest text-slate-400">Serviços</p>
          </div>
          {(data?.services || []).map((s, i) => (
            <div key={s.name} data-testid={`service-status-${i}`} className="flex items-center justify-between px-5 py-3.5 border-t border-slate-800/60">
              <span className="text-sm text-slate-200">{s.name}</span>
              <span className="flex items-center gap-2 text-xs">
                <Dot status={s.status} />
                <span className={s.status === "operational" ? "text-emerald-300" : "text-amber-300"}>
                  {s.status === "operational" ? "Operacional" : "Instável"}
                </span>
              </span>
            </div>
          ))}
        </div>

        <div>
          <p className="text-[11px] font-mono2 uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
            <Server size={13} className="text-cyan-400" /> Infraestrutura em tempo real
          </p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="routes-grid">
            {(data?.routes || []).map((r) => <RouteCard key={r.name} r={r} />)}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-600">
          Monitoramento em tempo real da infraestrutura CineBot · <Link to="/" className="text-cyan-400 hover:text-cyan-300">Voltar ao site</Link>
        </p>
      </div>
    </div>
  );
}
